/**
 * Generic DataTable Component
 *
 * Industry-standard reusable data table with:
 * - Column definitions
 * - Built-in pagination
 * - Sorting support
 * - Loading states
 * - Empty states
 * - Filters support
 */

import { ReactNode, useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table'
import { Pagination } from './pagination'
import { Skeleton } from './skeleton'
import { cn } from '@/lib/utils'

export interface ColumnDef<T = any> {
  key: string
  header: string | ReactNode
  cell?: (row: T, index: number) => ReactNode
  className?: string
  headerClassName?: string
  sortable?: boolean
}

export interface DataTableProps<T = any> {
  columns: ColumnDef<T>[]
  data: T[]
  isLoading?: boolean
  emptyMessage?: string
  emptyIcon?: ReactNode
  className?: string

  // Pagination props
  pagination?: {
    currentPage: number
    totalItems: number
    pageSize: number
    onPageChange: (page: number) => void
  }

  // Optional filters slot
  filters?: ReactNode

  // Row props
  onRowClick?: (row: T) => void
  rowClassName?: string | ((row: T) => string)
}

export function DataTable<T = any>({
  columns,
  data,
  isLoading = false,
  emptyMessage = 'No data available',
  emptyIcon,
  className,
  pagination,
  filters,
  onRowClick,
  rowClassName,
}: DataTableProps<T>) {
  // Loading state
  if (isLoading) {
    return (
      <div className={cn('space-y-4', className)}>
        {filters && <div className="mb-4">{filters}</div>}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead key={column.key} className={column.headerClassName}>
                    {typeof column.header === 'string' ? column.header : column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {columns.map((column) => (
                    <TableCell key={column.key}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className={cn('space-y-4', className)}>
        {filters && <div className="mb-4">{filters}</div>}
        <div className="rounded-md border">
          <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
            {emptyIcon && <div className="mb-4">{emptyIcon}</div>}
            <p>{emptyMessage}</p>
          </div>
        </div>
      </div>
    )
  }

  // Helper to get cell content
  const getCellContent = (row: T, column: ColumnDef<T>, index: number) => {
    if (column.cell) {
      return column.cell(row, index)
    }
    // Default: access property by key
    return (row as any)[column.key]
  }

  // Helper to get row class
  const getRowClass = (row: T) => {
    if (typeof rowClassName === 'function') {
      return rowClassName(row)
    }
    return rowClassName
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Filters slot */}
      {filters && <div>{filters}</div>}

      {/* Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.key} className={column.headerClassName}>
                  {typeof column.header === 'string' ? column.header : column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, index) => (
              <TableRow
                key={index}
                onClick={() => onRowClick?.(row)}
                className={cn(
                  onRowClick && 'cursor-pointer hover:bg-muted/50',
                  getRowClass(row)
                )}
              >
                {columns.map((column) => (
                  <TableCell key={column.key} className={column.className}>
                    {getCellContent(row, column, index)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalItems={pagination.totalItems}
          pageSize={pagination.pageSize}
          onPageChange={pagination.onPageChange}
        />
      )}
    </div>
  )
}
